import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import {
  banUser,
  getUserPhotos,
  listUsers,
  unbanUser,
  type UserRow,
} from '@/lib/admin';
import { telegramFileUrl } from '@/lib/telegram';
import { showToast } from '@/lib/toast';
import { Button } from '@/components/ui/Button';
import type { PhotoRow } from '@/types';
import { getDeviceId } from '@/lib/device';

function errorToString(err: unknown): string {
  if (err == null) return 'Eroare necunoscută';
  if (err instanceof Error) return err.message;
  if (typeof err === 'object') {
    const obj = err as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
  }
  return String(err);
}

function shortDeviceId(id: string): string {
  // Primele 4 si ultimele 4 caractere separate prin "…"
  if (id.length <= 12) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}z`;
  const mo = Math.floor(d / 30);
  return `${mo}l`;
}

export default function UsersPage() {
  const { role, loading: authLoading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [selected, setSelected] = useState<UserRow | null>(null);
  const [banModalFor, setBanModalFor] = useState<UserRow | null>(null);
  const [banReason, setBanReason] = useState('');
  const [filter, setFilter] = useState<'all' | 'banned' | 'with_photos'>('all');
  const [search, setSearch] = useState('');

  // Redirect la /admin daca nu suntem admin
  useEffect(() => {
    if (!authLoading && role === null) {
      navigate('/admin', { replace: true });
    }
  }, [authLoading, role, navigate]);

  const { data: users = [], isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
    enabled: role !== null,
    refetchInterval: 15_000,
  });

  // Diagnostic: vezi exact ce vine din query
  // eslint-disable-next-line no-console
  console.log('[UsersPage] state:', { usersCount: users.length, role, queryError: queryError?.message });

  const filtered = useMemo(() => {
    let out = users;
    if (filter === 'banned') out = out.filter((u) => u.is_banned);
    else if (filter === 'with_photos')
      out = out.filter((u) => u.photos_total > 0 || u.comments_count > 0);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (u) =>
          (u.display_name ?? '').toLowerCase().includes(q) ||
          u.device_id.toLowerCase().includes(q),
      );
    }
    return out;
  }, [users, filter, search]);

  const totalBanned = users.filter((u) => u.is_banned).length;
  const totalWithPhotos = users.filter((u) => u.photos_total > 0).length;

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['user-photos', selected?.device_id] });
  }, [qc, selected]);

  const onConfirmBan = useCallback(async () => {
    if (!banModalFor) return;
    try {
      const res = await banUser(banModalFor.device_id, banReason.trim() || undefined);
      showToast(
        `User suspendat (${res.rejectedPhotos} poze respinse, ${res.deletedComments} comentarii șterse)`,
        'success',
      );
      setBanModalFor(null);
      setBanReason('');
      if (selected?.device_id === banModalFor.device_id) {
        setSelected(null);
      }
      refresh();
    } catch (e) {
      showToast(errorToString(e) || 'Eroare la suspendare', 'error');
    }
  }, [banModalFor, banReason, refresh, selected]);

  const onUnban = useCallback(
    async (user: UserRow) => {
      try {
        await unbanUser(user.device_id);
        showToast(`Suspendare ridicată pentru ${user.display_name ?? shortDeviceId(user.device_id)}`, 'success');
        refresh();
      } catch (e) {
        showToast(errorToString(e) || 'Eroare', 'error');
      }
    },
    [refresh],
  );

  if (authLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-accent" />
      </main>
    );
  }

  return (
    <main className="min-h-screen px-md py-lg bg-transparent pb-24">
      {/* Header */}
      <div className="container-page flex items-end justify-between gap-md pb-md border-b border-border">
        <div>
          <p className="eyebrow">Panou Moderare</p>
          <h1 className="font-serif text-h2 text-fg">Useri</h1>
          <p className="mt-xs text-sm text-fg-soft">
            {users.length} {users.length === 1 ? 'vizitator' : 'vizitatori'}
            {totalBanned > 0 && (
              <>
                {' · '}
                <span className="text-error">{totalBanned} suspendați</span>
              </>
            )}
            {totalWithPhotos > 0 && (
              <>
                {' · '}
                <span>{totalWithPhotos} cu poze</span>
              </>
            )}
          </p>
          {/* Diagnostic — vizibil doar cand nu sunt useri (ca sa intelegem de ce) */}
          {users.length === 0 && !isLoading && (
            <div className="mt-md rounded border border-accent/40 bg-accent/5 p-sm text-xs font-mono">
              <p>device_id curent: <span className="text-accent">{getDeviceId() ?? 'LIPSĂ'}</span></p>
              <p>role curent: <span className="text-accent">{role ?? 'null'}</span></p>
              <p>query error: <span className="text-error">{queryError?.message ?? '—'}</span></p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-xs underline text-fg-soft hover:text-fg"
              >
                Reîncarcă
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-sm">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/moderate')}>
            ← Moderare
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              await logout();
              navigate('/admin');
            }}
          >
            Delogare
          </Button>
        </div>
      </div>

      {/* Filter + search */}
      <div className="container-page mt-md flex flex-wrap items-center gap-sm">
        <div className="flex gap-xs rounded-lg bg-bg-alt p-1 border border-border">
          {(['all', 'with_photos', 'banned'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-md px-md py-1.5 text-sm font-medium transition-all ${
                filter === f
                  ? 'bg-accent text-bg-alt shadow-sm'
                  : 'text-fg-soft hover:text-fg'
              }`}
            >
              {f === 'all' && 'Toți'}
              {f === 'with_photos' && 'Cu activitate'}
              {f === 'banned' && `Suspendați (${totalBanned})`}
            </button>
          ))}
        </div>

        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Caută după nume sau device id…"
          className="flex-1 min-w-[200px] rounded-md border border-border bg-bg px-md py-1.5 text-sm placeholder:text-fg-soft/60 focus:outline-none focus:ring-2 focus:ring-accent/40"
        />
      </div>

      {/* List */}
      <div className="container-page mt-md">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton h-16 rounded-md" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-2xl text-center"
          >
            <p className="font-serif text-h3 text-fg">
              {filter === 'banned' ? 'Niciun user suspendat' : 'Niciun user găsit'}
            </p>
            <p className="mt-sm text-fg-soft">
              {filter === 'banned'
                ? 'Când vei suspenda pe cineva, va apărea aici.'
                : 'Când vizitatorii intră pe site, vor apărea aici.'}
            </p>
          </motion.div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-bg-alt">
            <table className="w-full text-sm">
              <thead className="bg-bg border-b border-border">
                <tr className="text-left">
                  <th className="px-md py-sm font-medium text-fg-soft">User</th>
                  <th className="px-md py-sm font-medium text-fg-soft">Device</th>
                  <th className="px-md py-sm font-medium text-fg-soft text-right">Poze</th>
                  <th className="px-md py-sm font-medium text-fg-soft text-right">Com.</th>
                  <th className="px-md py-sm font-medium text-fg-soft text-right">Likes</th>
                  <th className="px-md py-sm font-medium text-fg-soft">Ultima</th>
                  <th className="px-md py-sm font-medium text-fg-soft text-right">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isSelected = selected?.device_id === u.device_id;
                  return (
                    <tr
                      key={u.device_id}
                      onClick={() => setSelected(u)}
                      className={`cursor-pointer border-b border-border/50 last:border-b-0 transition-colors ${
                        isSelected ? 'bg-accent/10' : 'hover:bg-bg-alt/60'
                      }`}
                    >
                      <td className="px-md py-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-fg">
                            {u.display_name ?? <span className="text-fg-soft italic">anonim</span>}
                          </span>
                          {u.is_banned && (
                            <span className="rounded-full bg-error/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-error">
                              suspendat
                            </span>
                          )}
                          {u.role === 'admin' && (
                            <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent">
                              admin
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-md py-sm font-mono text-xs text-fg-soft">
                        {shortDeviceId(u.device_id)}
                      </td>
                      <td className="px-md py-sm text-right font-mono text-fg">
                        {u.photos_total > 0 ? (
                          <span>
                            {u.photos_total}
                            {u.photos_pending > 0 && (
                              <span className="ml-1 text-xs text-fg-soft">
                                ({u.photos_pending} pending)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-fg-soft">—</span>
                        )}
                      </td>
                      <td className="px-md py-sm text-right font-mono text-fg">
                        {u.comments_count > 0 ? u.comments_count : <span className="text-fg-soft">—</span>}
                      </td>
                      <td className="px-md py-sm text-right font-mono text-fg">
                        {u.likes_given > 0 ? u.likes_given : <span className="text-fg-soft">—</span>}
                      </td>
                      <td className="px-md py-sm text-xs text-fg-soft">
                        {relativeTime(u.last_seen)}
                      </td>
                      <td className="px-md py-sm text-right" onClick={(e) => e.stopPropagation()}>
                        {u.is_banned ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onUnban(u)}
                            className="text-accent"
                          >
                            Ridică
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={u.role === 'admin' || u.role === 'super_admin'}
                            onClick={() => {
                              setBanModalFor(u);
                              setBanReason('');
                            }}
                            className="border-error/40 text-error hover:bg-error/10 hover:border-error disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            Suspendă
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer cu poze user */}
      <AnimatePresence>
        {selected && (
          <UserDrawer
            user={selected}
            onClose={() => setSelected(null)}
            onBanClick={(u) => {
              setBanModalFor(u);
              setBanReason('');
            }}
            onUnbanClick={onUnban}
          />
        )}
      </AnimatePresence>

      {/* Modal confirmare ban */}
      <AnimatePresence>
        {banModalFor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-overlay flex items-center justify-center bg-black/50 p-md backdrop-blur-sm"
            onClick={() => setBanModalFor(null)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-lg border border-border bg-bg p-lg shadow-2xl"
            >
              <h3 className="font-serif text-h3 text-fg">Suspendă user</h3>
              <p className="mt-sm text-sm text-fg-soft">
                {banModalFor.display_name ?? shortDeviceId(banModalFor.device_id)} va fi suspendat.
                Pozele lui vor fi respinse și comentariile șterse. Likes-urile și view-urile date de el
                la pozele altora rămân.
              </p>
              <textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Motiv (opțional, vizibil doar pentru admini)"
                className="mt-md w-full rounded-md border border-border bg-bg-alt p-sm text-sm placeholder:text-fg-soft/60 focus:outline-none focus:ring-2 focus:ring-accent/40"
                rows={3}
              />
              <div className="mt-md flex justify-end gap-sm">
                <Button variant="ghost" size="sm" onClick={() => setBanModalFor(null)}>
                  Anulează
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onConfirmBan}
                  className="bg-error text-bg-alt hover:brightness-110"
                >
                  Suspendă
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

// =============================================================
// Drawer lateral — afișează toate pozele userului (pending + approved)
// =============================================================

function UserDrawer({
  user,
  onClose,
  onBanClick,
  onUnbanClick,
}: {
  user: UserRow;
  onClose: () => void;
  onBanClick: (u: UserRow) => void;
  onUnbanClick: (u: UserRow) => void;
}) {
  const navigate = useNavigate();
  const { data: photos = [], isLoading } = useQuery({
    queryKey: ['user-photos', user.device_id],
    queryFn: () => getUserPhotos(user.device_id),
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.aside
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 280 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto bg-bg shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-border bg-bg/95 backdrop-blur px-lg py-md">
          <div className="flex items-start justify-between gap-md">
            <div>
              <p className="eyebrow text-xs">User</p>
              <h2 className="font-serif text-h3 text-fg">
                {user.display_name ?? <span className="text-fg-soft italic">anonim</span>}
              </h2>
              <p className="mt-xs font-mono text-xs text-fg-soft">
                {user.device_id}
              </p>
              <div className="mt-xs flex gap-sm text-xs text-fg-soft">
                <span>Prima vizită: {new Date(user.first_seen).toLocaleDateString('ro-RO')}</span>
                <span>·</span>
                <span>{user.page_views} pagini văzute</span>
                <span>·</span>
                <span>{user.likes_given} like-uri date</span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-2xl text-fg-soft hover:text-fg"
              aria-label="Închide"
            >
              ×
            </button>
          </div>

          {user.is_banned && (
            <div className="mt-md rounded-md border border-error/40 bg-error/10 p-sm text-sm">
              <p className="font-medium text-error">User suspendat</p>
              {user.suspended_reason && (
                <p className="mt-xs text-fg-soft">Motiv: {user.suspended_reason}</p>
              )}
              {user.suspended_at && (
                <p className="mt-xs text-xs text-fg-soft">
                  Din {new Date(user.suspended_at).toLocaleString('ro-RO')}
                </p>
              )}
            </div>
          )}

          <div className="mt-md flex gap-sm">
            {user.is_banned ? (
              <Button variant="primary" size="sm" onClick={() => onUnbanClick(user)}>
                Ridică suspendarea
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={user.role === 'admin' || user.role === 'super_admin'}
                onClick={() => onBanClick(user)}
                className="border-error/40 text-error hover:bg-error/10 hover:border-error disabled:opacity-30"
              >
                Suspendă user
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onClose();
                navigate(`/admin/moderate?uploader=${encodeURIComponent(user.device_id)}`);
              }}
            >
              Vezi la moderare →
            </Button>
          </div>
        </div>

        {/* Content: grid poze */}
        <div className="px-lg py-md">
          <h3 className="font-serif text-h3 text-fg">
            Pozele userului ({photos.length})
          </h3>

          {isLoading ? (
            <div className="mt-md grid grid-cols-2 gap-sm sm:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton aspect-square rounded-md" />
              ))}
            </div>
          ) : photos.length === 0 ? (
            <p className="mt-md text-sm text-fg-soft">Nicio poză încărcată.</p>
          ) : (
            <div className="mt-md grid grid-cols-2 gap-sm sm:grid-cols-3">
              {photos.map((p: PhotoRow) => (
                <div
                  key={p.id}
                  className="relative aspect-square overflow-hidden rounded-md border border-border bg-bg-alt"
                >
                  {p.telegram_file_id ? (
                    <img
                      src={telegramFileUrl(p.telegram_file_id)}
                      alt={p.caption || ''}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-fg-soft">
                      fără imagine
                    </div>
                  )}
                  <span
                    className={`absolute top-1.5 left-1.5 rounded px-1.5 py-0.5 text-[10px] font-mono uppercase ${
                      p.status === 'approved'
                        ? 'bg-success/90 text-bg-alt'
                        : p.status === 'pending'
                        ? 'bg-accent/90 text-bg-alt'
                        : 'bg-error/90 text-bg-alt'
                    }`}
                  >
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.aside>
    </motion.div>
  );
}